import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import '@/styles/milk-landing.css';

const SERIF = "'Instrument Serif', Georgia, serif";

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#4C73A6" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" style={{ width: 17, height: 17, flexShrink: 0, marginTop: 2 }}>
    <path d="m5 12 5 5L20 6" />
  </svg>
);

const TIERS = [
  {
    id: 'free', name: 'Starter', price: '$0', cadence: 'forever',
    tagline: 'For getting a feel for capture-to-card.',
    cta: 'Get started free', highlight: false,
    features: ['Up to 10 Action Cards / month', 'Audio & text capture', 'AI summaries & tasks', 'Personal workspace', 'Email follow-up drafts'],
  },
  {
    id: 'pro', name: 'Pro', price: '$18', cadence: 'per user / month',
    tagline: 'For professionals who live in conversations.',
    cta: 'Start Pro trial', highlight: true,
    features: ['Unlimited Action Cards', 'Meeting bot recording (Recall)', 'Speaker diarization', 'Interactive digest emails', 'Contacts & follow-up tracking', 'Priority support'],
  },
  {
    id: 'team', name: 'Team', price: '$32', cadence: 'per user / month',
    tagline: 'For cross-functional teams that ship.',
    cta: 'Talk to us', highlight: false,
    features: ['Everything in Pro', 'Shared workspaces & teams', 'Permission layers & accountability', 'Audit trails & activity views', 'Admin console & analytics', 'SSO & advanced security'],
  },
];

const FAQ = [
  { q: 'Is there really a free plan?', a: 'Yes. The Starter plan is free forever and includes up to 10 Action Cards a month — enough to see how Milk fits your workflow. No credit card required.' },
  { q: 'Can I change plans later?', a: 'Anytime. Upgrade, downgrade, or cancel from your settings. Changes take effect at the start of your next billing cycle.' },
  { q: 'How does billing work for teams?', a: 'Team plans are billed per active user, per month. You can add or remove seats as your team changes, and we prorate the difference.' },
  { q: 'Do you offer annual pricing?', a: 'Yes — annual billing saves two months versus monthly. Reach out and we\u2019ll set it up for your workspace.' },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const goSignup = () => navigate(user ? '/' : '/register');

  return (
    <div className="mlk-land" data-testid="pricing-page">
      <MarketingHeader linkPrefix="/#" />

      <main>
        {/* HERO */}
        <section style={{ padding: '72px 0 32px', textAlign: 'center' }}>
          <div className="container" style={{ maxWidth: 760 }}>
            <p className="eyebrow">Pricing</p>
            <h1 style={{ fontFamily: SERIF, fontSize: 54, lineHeight: 1.05, color: '#212E40', margin: '14px 0 16px' }}>
              Simple, honest pricing.
            </h1>
            <p style={{ fontSize: 19, lineHeight: 1.6, color: '#5A6675', maxWidth: 560, margin: '0 auto' }}>
              Start free. Upgrade when Milk is saving you hours every week — not before. No hidden fees, cancel anytime.
            </p>
          </div>
        </section>

        {/* TIERS */}
        <section style={{ padding: '16px 0 24px' }}>
          <div className="container">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 22, alignItems: 'stretch' }}>
              {TIERS.map((t) => (
                <div
                  key={t.id}
                  data-testid={`pricing-tier-${t.id}`}
                  style={{
                    background: t.highlight ? '#212E40' : '#fff',
                    color: t.highlight ? '#F4F2EB' : '#212E40',
                    border: t.highlight ? 'none' : '1px solid rgba(33,46,64,.10)',
                    borderRadius: 20,
                    padding: '32px 28px',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: t.highlight ? '0 22px 48px rgba(33,46,64,.22)' : '0 1px 2px rgba(33,46,64,.04)',
                    transform: t.highlight ? 'translateY(-6px)' : 'none',
                  }}
                >
                  {t.highlight && (
                    <span style={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: '#D9A94E', background: 'rgba(217,169,78,.14)', padding: '5px 11px', borderRadius: 999, marginBottom: 16 }}>Most popular</span>
                  )}
                  <h3 style={{ fontFamily: SERIF, fontSize: 30 }}>{t.name}</h3>
                  <p style={{ fontSize: 14.5, color: t.highlight ? 'rgba(244,242,235,.72)' : '#5A6675', margin: '6px 0 20px' }}>{t.tagline}</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 24 }}>
                    <span style={{ fontFamily: SERIF, fontSize: 46, lineHeight: 1 }}>{t.price}</span>
                    <span style={{ fontSize: 14, color: t.highlight ? 'rgba(244,242,235,.66)' : '#8A93A0' }}>{t.cadence}</span>
                  </div>
                  <button
                    className={`btn btn-lg ${t.highlight ? 'btn-cream' : 'btn-primary'}`}
                    onClick={goSignup}
                    data-testid={`pricing-cta-${t.id}`}
                    style={{ width: '100%', justifyContent: 'center', marginBottom: 26 }}
                  >
                    {t.cta}
                  </button>
                  <div style={{ display: 'grid', gap: 13 }}>
                    {t.features.map((f, i) => (
                      <div key={i} style={{ display: 'flex', gap: 10, fontSize: 15, lineHeight: 1.5, color: t.highlight ? 'rgba(244,242,235,.9)' : '#3C4654' }}>
                        <Check /> <span>{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p style={{ textAlign: 'center', fontSize: 14, color: '#8A93A0', marginTop: 24 }}>
              Prices in USD. Team plans billed per active user. Annual billing saves two months.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ padding: '48px 0 24px' }}>
          <div className="container" style={{ maxWidth: 780 }}>
            <h2 style={{ fontFamily: SERIF, fontSize: 38, color: '#212E40', textAlign: 'center', marginBottom: 32 }}>Questions, answered</h2>
            <div style={{ display: 'grid', gap: 14 }}>
              {FAQ.map((f, i) => (
                <div key={i} data-testid={`pricing-faq-${i}`} style={{ background: '#fff', border: '1px solid rgba(33,46,64,.08)', borderRadius: 16, padding: '22px 24px' }}>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: '#212E40', marginBottom: 8 }}>{f.q}</h3>
                  <p style={{ fontSize: 15.5, lineHeight: 1.7, color: '#5A6675' }}>{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta" style={{ padding: '48px 0 72px' }}>
          <div className="container">
            <div className="cta-band">
              <h2>Start free today.</h2>
              <p>No credit card. Set up in two minutes. Upgrade only when it's worth it.</p>
              <div className="cta-actions">
                <button className="btn btn-cream btn-lg" onClick={goSignup} data-testid="pricing-footer-cta">Get started free</button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter linkPrefix="/#" />
    </div>
  );
}
